// Product structures → the BOM Viewer's export (2026-11-01).
//
// The Export window and every format it writes (JSON / CSV / HTML / PDF, Hierarchic or
// Summary, the zip with DMS files + attachments) live in bom/tree/exportUtils.js and read a
// flat parent-before-child list of { nodeKey, parentKey, depth, isRoot, row }, where `row`
// answers byName / byTitle like a normalised Orcanos row. Here that list is built from
// `GET /structures/export-rows`: the hierarchy is OURS (bom_links), the fields are the item's
// Part Catalog / DMS filter row. So the structure's own values are answered as fields:
//   Quantity            — the link's qty (the root is one unit)
//   Unit cost           — the item's own cost on a leaf, 0 on an assembly, so the export's
//                          rollup gives the same total as Structure.unit_cost
//   __ps_* columns      — revision / status / find no. / ref. des. / note of the structure
//   User_Prefix/Obj_name — fall back to the stored key / name (a missing item, a baseline)
import { mergeRowColumns, normalizeColKey } from '../utils/columns';
import { findUnitCostField, UNIT_COST_KEY, COST_KEY, TOTAL_COST_KEY } from '../utils/cost';

const PS_UNIT_COST = '__ps_unit_cost';

// Filter fields the structure already answers (or that mean nothing in an export).
const HIDDEN = new Set([
  'copy as link', 'in pool', 'is branch', 'original id', 'id', 'master part source',
  'quantity', 'revision', 'part revision',
]);
const STATUS = new Set(['status', 'part status', 'routing state', 'document status']);
const CORE = new Set(['key', 'user prefix', 'name', 'obj name']);

function fieldText(f) {
  return (f && (f.Display_text || f.Display || f.Text || f.Value || '')) || '';
}

function makeRow(r) {
  const own = [...(r.fields || [])].sort((a, b) => parseInt(a.Web_order || 0, 10) - parseInt(b.Web_order || 0, 10));
  const fields = [{ Name: PS_UNIT_COST, Title: 'Unit cost', Display_text: String(r.own_cost || 0) }, ...own];
  const synth = {
    Quantity: r.depth > 0 ? String(r.qty ?? 1) : '1',
    __ps_rev: r.rev || '',
    __ps_status: r.missing ? 'Missing' : (r.status || ''),
    __ps_find: r.find_no || '',
    __ps_refdes: r.ref_des || '',
    __ps_note: r.note || '',
  };
  const byName = (n) => {
    if (n in synth) return synth[n];
    const v = fieldText(fields.find((f) => f.Name === n));
    if (v) return v;
    if (n === 'User_Prefix') return r.key || '';
    if (n === 'Obj_name') return r.name || '';
    return '';
  };
  const byTitle = (t) => {
    if (t === 'Quantity') return synth.Quantity;
    const v = fieldText(fields.find((f) => f.Title === t));
    if (v) return v;
    if (t === 'Key') return r.key || '';
    if (t === 'Name') return r.name || '';
    return '';
  };
  return {
    id: r.id, itemId: String(r.id || ''), originalId: String(r.id || ''), type: r.type || '',
    fields, byName, byTitle, objName: r.name || '', userPrefix: r.key || '', raw: {},
    hasChildren: undefined,
  };
}

/** Rows from export-rows → the export's node list (already in tree order, parent first). */
export function psNodes(rows) {
  return rows.map((r) => ({
    nodeKey: r.rk, parentKey: r.parent_rk || null, depth: r.depth, isRoot: r.depth === 0, row: makeRow(r),
  }));
}

/** The column list the Export window offers: the structure's own, then every filter field. */
export function psColumns(nodes, { baseline = false } = {}) {
  // A baseline froze its status; the live status field would contradict it, so it is left out there.
  const costField = findUnitCostField(nodes.flatMap((n) => n.row.fields.slice(1)));
  const skip = (n, t) => n === normalizeColKey(PS_UNIT_COST)
    || (costField && (n === normalizeColKey(costField.Name) || (t && t === normalizeColKey(costField.Title))))
    || HIDDEN.has(n) || HIDDEN.has(t) || (baseline && (STATUS.has(n) || STATUS.has(t)));
  const dynamic = mergeRowColumns(nodes.map((n) => n.row.fields), { skip });
  const isCore = (c) => CORE.has(normalizeColKey(c.key)) || CORE.has(normalizeColKey(c.title));
  const hasStatusField = dynamic.some((c) => STATUS.has(normalizeColKey(c.key)) || STATUS.has(normalizeColKey(c.title)));
  // Key and Name always exist (stored on the structure), even when the filter does not select them.
  const core = dynamic.filter(isCore);
  if (!core.some((c) => ['key', 'user prefix'].includes(normalizeColKey(c.key)) || normalizeColKey(c.title) === 'key')) {
    core.unshift({ key: 'User_Prefix', title: 'Key' });
  }
  if (!core.some((c) => ['name', 'obj name'].includes(normalizeColKey(c.key)) || normalizeColKey(c.title) === 'name')) {
    core.splice(1, 0, { key: 'Obj_name', title: 'Name' });
  }
  return [
    ...core,
    { key: '__ps_rev', title: 'Revision' },
    ...(baseline || !hasStatusField ? [{ key: '__ps_status', title: 'Status' }] : []),
    { key: '__quantity', title: 'Quantity' },
    { key: '__ps_find', title: 'Find no.' },
    { key: '__ps_refdes', title: 'Ref. des.' },
    { key: UNIT_COST_KEY, title: 'Unit Cost' },
    { key: COST_KEY, title: 'Cost' },
    { key: TOTAL_COST_KEY, title: 'Total Cost' },
    ...dynamic.filter((c) => !isCore(c)),
    { key: '__ps_note', title: 'Note' },
  ];
}
